import React from "react";
import { JsonInput } from "react-admin-json-view";
import {
  AutocompleteInput,
  ChipField,
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  List,
  ReferenceField,
  ReferenceInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
  useNotify,
} from "react-admin";
import { useFormState } from "react-final-form";

// Helper function for name search
const nameLikeSearch = (n) => {
  if (n && n.length >= 2) {
    return {
      name__like: n,
    };
  }
};

// Helper function for username search
const usernameLikeSearch = (u) => {
  if (u && u.length >= 2) {
    return {
      username__like: u,
    };
  }
};

const validateJSON = (value) => {
  console.log('32', value);
  if (!value) return "The config is required";
  try {
    JSON.parse(typeof value === "string" ? value : JSON.stringify(value));
    return undefined;
  } catch (e) {
    return "Invalid JSON format: " + e.message;
  }
};

const removeTypename = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(removeTypename);
  } else if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      if (key !== '__typename') {
        newObj[key] = removeTypename(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
};

const allowedConfigKeys = new Set([
  'schema',
  'mapValues',
  'addColumns',
  'deleteRows',
  'deleteColumns',
  'fileMatchCriteria',
  'ignoreFile',
  'updateIfExists',
  'useUSFormat',
  'sendFailedFilesToAI',
  'alwaysReviewAIResults',
  'skipAI',
]);

const splitConfig = (config) => {
  if (!config || typeof config !== 'object') {
    return { typedConfig: config, rawConfig: config };
  }

  const typedConfig = {};
  allowedConfigKeys.forEach((key) => {
    if (key in config) {
      typedConfig[key] = config[key];
    }
  });

  return { typedConfig, rawConfig: config };
};

const ConfigJsonInput = () => {
  return (
    <JsonInput
      source="config"
      onChange={() => {}}
      reactJsonOptions={{
        name: null,
        collapsed: 1,
        enableClipboard: false,
        displayDataTypes: false,
      }}
      format={(value) => {
        try{
          if(typeof value === 'string') {
            return JSON.parse(value);
          } else {
            return value;
          }
        } catch (e) {
          return value;
        } 
      }}
      fullWidth
    />
  );
};

// Company filter component
const CompanyFilter = (props) => (
  <ReferenceInput
    label="Company"
    source="companyId"
    reference="Company"
    filterToQuery={nameLikeSearch}
    alwaysOn
    fullWidth
    allowEmpty
    perPage={50}
    {...props}
  >
    <AutocompleteInput source="name" />
  </ReferenceInput>
);

// Project filter component that depends on selected company
const ProjectFilter = (props) => {
  const { values } = useFormState();
  return (
    <ReferenceInput
      enabled={values["company.id"]}
      label="Project"
      source="projectId"
      reference="Project"
      filterToQuery={nameLikeSearch}
      alwaysOn
      fullWidth
      allowEmpty
      perPage={50}
      {...props}
    >
      <AutocompleteInput source="name" />
    </ReferenceInput>
  );
};

const ImportConfigFilters = [
  <TextInput label="Config Name" source="name__like" alwaysOn fullWidth />,
  <CompanyFilter alwaysOn />,
  <ProjectFilter alwaysOn />,
];

export const ImportConfigList = (props) => (
  <List {...props} filters={ImportConfigFilters}>
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="name" />
      <ReferenceField
        label="Company"
        reference="Company"
        source="company.id"
        link={false}
      >
        <ChipField source="name" />
      </ReferenceField>
      <ReferenceField
        label="Project"
        reference="Project"
        source="project.id"
        link={false}
      >
        <ChipField source="name" />
      </ReferenceField>
      <ReferenceField
        label="Created By"
        reference="User"
        source="createdBy.id"
        link={false}
      >
        <ChipField source="username" />
      </ReferenceField>
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

export const ImportConfigEdit = (props) => {
  const notify = useNotify();


  const transformSubmit = (data) => {
    const cleanedConfig = typeof data.config === "string"
    ? removeTypename(JSON.parse(data.config.replace(/\\n/g, '')))
    : removeTypename(data.config);

    const { typedConfig, rawConfig } = splitConfig(cleanedConfig);
    try {
      const transformed = {
        name: data.name,
        config: typedConfig,
        configRaw: rawConfig,
        companyId: data.company?.id || null,
        projectId: data.project?.id || null,
        userId: data.createdBy?.id || null,
      };
      return transformed;
    } catch (error) {
      notify("Invalid JSON format in config", { type: "error" });
      throw error;
    }
  };

  return (
    <Edit {...props} transform={transformSubmit}>
      <SimpleForm>
        <TextField source="id" />
        <TextInput source="name" fullWidth />
        <ReferenceInput
          label="Company"
          filterToQuery={nameLikeSearch}
          source="company.id"
          reference="Company"
          fullWidth
          perPage={50}
          allowEmpty
        >
          <AutocompleteInput source="name" />
        </ReferenceInput>
        <ReferenceInput
          label="Project"
          filterToQuery={nameLikeSearch}
          source="project.id"
          reference="Project"
          format={(value) => (value === "" ? null : value)}
          fullWidth
          perPage={50}
          allowEmpty
        >
          <AutocompleteInput source="name" />
        </ReferenceInput>
        <ReferenceInput
          label="User"
          source="createdBy.id"
          reference="User"
          filterToQuery={usernameLikeSearch}
          fullWidth
          perPage={50}
          allowEmpty
        >
          <AutocompleteInput optionText="username" />
        </ReferenceInput>
        {/* <ConfigJsonInput /> */}
        <TextInput
          source="config"
          multiline
          fullWidth
          validate={validateJSON}
          format={(value) => {
            try {
              if(typeof value === 'object') {
                const cleaned = removeTypename(value);
                return JSON.stringify(cleaned, null, 2);
              } else {
                return value;
              }
            } catch (e) {
              return value;
            }
          }}
          options={{
            style: {
              fontFamily: "monospace",
            },
          }}
          InputProps={{
            style: {
              minHeight: "300px",
            },
            onKeyDown: (e) => {
              e.stopPropagation();
            },
         
          }}
        />
      </SimpleForm>
    </Edit>
  );
};


export const ImportConfigCreate = (props) => (
  <Create title="Create Import Config" {...props} transform={(data) => {
    const cleanedConfig = typeof data.config === "string"
      ? removeTypename(JSON.parse(data.config.replace(/\\n/g, '')))
      : removeTypename(data.config);
    const { typedConfig, rawConfig } = splitConfig(cleanedConfig);

    return {
      ...data,
      config: typedConfig,
      configRaw: rawConfig,
      companyId: data.company?.id || null,
      projectId: data.project?.id || null,
    };
  }}>
    <SimpleForm>
      <TextInput source="name" fullWidth />
      <ReferenceInput
        filterToQuery={nameLikeSearch}
        label="Company"
        source="company.id"
        reference="Company"
        perPage={100}
        fullWidth
      >
        <AutocompleteInput source="name" />
      </ReferenceInput>
      <ReferenceInput
        perPage={100}
        filterToQuery={nameLikeSearch}
        label="Project"
        source="project.id"
        reference="Project"
        fullWidth
        allowEmpty
      >
        <AutocompleteInput source="name"/>
      </ReferenceInput>
      <TextInput
        source="config"
        multiline
        fullWidth
        validate={validateJSON}
        options={{
          style: {
            fontFamily: "monospace",
          },
        }}
        InputProps={{
          style: {
            minHeight: "300px",
          },
        }}
      />
    </SimpleForm>
  </Create>
);
